/**
 * MutationTimeline - Visual history of funnel changes (MA-MUTATION-UI)
 * 
 * Displays a chronological timeline of all mutations applied to a funnel:
 * - What changed (summary)
 * - When it changed (timestamp)
 * - Who made the change (actor)
 * - Version progression
 * - Status (applied, undone, redone)
 * - Compare versions (diff view)
 * 
 * Data source: /api/funnels/{id}/mutations
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  History,
  GitCommit,
  GitCompare,
  User,
  Bot,
  Clock,
  ChevronDown,
  ChevronUp,
  Undo2,
  Redo2,
  Check,
  X,
  Loader2,
  AlertCircle,
  Sparkles,
  Edit3,
  Plus,
  Trash2,
  Link2,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMutationHistory } from '@/hooks/useFunnels';
import { formatDistanceToNow, format } from 'date-fns';
import { CompareVersionButton } from './VersionCompare';

// Operation type icons and colors
const OP_CONFIG = {
  add_node: { icon: Plus, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Add' },
  update_node: { icon: Edit3, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Update' },
  delete_node: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Delete' },
  add_edge: { icon: Link2, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Connect' },
  delete_edge: { icon: Link2, color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Disconnect' },
  update_settings: { icon: Settings, color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'Settings' },
  default: { icon: GitCommit, color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'Change' },
};

// Status badge configuration
const STATUS_CONFIG = {
  applied: { color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', label: 'Applied' },
  undone: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', label: 'Undone' },
  redone: { color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', label: 'Redone' },
  failed: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', label: 'Failed' },
};

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp) {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  return {
    relative: formatDistanceToNow(date, { addSuffix: true }),
    absolute: format(date, 'MMM d, yyyy h:mm a'),
  };
}

/**
 * Parse summary to extract operation type
 */
function parseOperation(summary) {
  const lower = summary?.toLowerCase() || '';
  if (lower.includes('add') && lower.includes('node')) return 'add_node';
  if (lower.includes('update') || lower.includes('edit') || lower.includes('change')) return 'update_node';
  if (lower.includes('delete') || lower.includes('remove')) return 'delete_node';
  if (lower.includes('edge') && lower.includes('add')) return 'add_edge';
  if (lower.includes('edge') && (lower.includes('delete') || lower.includes('remove'))) return 'delete_edge';
  if (lower.includes('setting')) return 'update_settings';
  return 'default';
}

/**
 * Single mutation entry in the timeline
 */
function MutationEntry({ mutation, isFirst, isLast, funnelId }) {
  const [expanded, setExpanded] = useState(false);
  
  const opType = parseOperation(mutation.summary);
  const opConfig = OP_CONFIG[opType] || OP_CONFIG.default;
  const OpIcon = opConfig.icon;
  
  const statusConfig = STATUS_CONFIG[mutation.status] || STATUS_CONFIG.applied;
  const time = formatTimestamp(mutation.created_at);
  
  const isAI = mutation.actor_type === 'ai' || mutation.actor_type === 'system';
  const ActorIcon = isAI ? Sparkles : User;
  
  return (
    <div 
      className="relative pl-8"
      data-testid={`mutation-entry-${mutation.mutation_id}`}
    >
      {/* Timeline connector */}
      {!isLast && (
        <div className="absolute left-[11px] top-8 bottom-0 w-0.5 bg-white/10" />
      )}
      
      {/* Timeline node */}
      <div className={cn(
        "absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center",
        opConfig.bg,
        "border border-white/10"
      )}>
        <OpIcon className={cn("h-3 w-3", opConfig.color)} />
      </div>
      
      {/* Content card */}
      <div className={cn(
        "mb-4 p-3 rounded-lg border transition-colors",
        "bg-white/[0.02] border-white/[0.08]",
        "hover:bg-white/[0.04] hover:border-white/[0.12]"
      )}>
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Summary */}
            <p className="text-sm font-medium text-slate-100 truncate">
              {mutation.summary || 'Funnel mutation'}
            </p>
            
            {/* Metadata row */}
            <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
              {/* Actor */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1">
                      <ActorIcon className={cn(
                        "h-3 w-3",
                        isAI ? "text-purple-400" : "text-slate-400"
                      )} />
                      {isAI ? 'AI' : 'User'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {mutation.actor_id || (isAI ? 'AI Assistant' : 'Unknown user')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {/* Version */}
              <span className="flex items-center gap-1">
                <GitCommit className="h-3 w-3" />
                v{mutation.base_version} → v{mutation.new_version}
              </span>
              
              {/* Time */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {time.relative}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {time.absolute}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          
          {/* Status badge and Compare button */}
          <div className="flex items-center gap-2">
            {/* Compare button */}
            {funnelId && mutation.base_version !== undefined && mutation.new_version !== undefined && (
              <CompareVersionButton
                funnelId={funnelId}
                baseVersion={mutation.base_version}
                compareVersion={mutation.new_version}
              />
            )}
            
            <Badge 
              variant="outline"
              className={cn(
                "text-[10px] px-1.5",
                statusConfig.color,
                statusConfig.border
              )}
            >
              {mutation.status === 'undone' && <Undo2 className="h-2.5 w-2.5 mr-1" />}
              {mutation.status === 'redone' && <Redo2 className="h-2.5 w-2.5 mr-1" />}
              {mutation.status === 'applied' && <Check className="h-2.5 w-2.5 mr-1" />}
              {mutation.status === 'failed' && <X className="h-2.5 w-2.5 mr-1" />}
              {statusConfig.label}
            </Badge>
          </div>
        </div>
        
        {/* Execution time (collapsed) */}
        {mutation.execution_time_ms && (
          <div className="mt-2 text-[10px] text-slate-500">
            Executed in {mutation.execution_time_ms.toFixed(1)}ms
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main Timeline Component
 */
export function MutationTimeline({ 
  funnelId, 
  className = '',
  maxHeight = '500px',
  compact = false,
}) {
  const { data: mutations, isLoading, error, refetch } = useMutationHistory(funnelId, 100);
  
  // Loading state
  if (isLoading) {
    return (
      <Card className={cn("glass border-white/10", className)} data-testid="mutation-timeline-loading">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
            <span className="ml-2 text-slate-400 text-sm">Loading history...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Card className={cn("glass border-white/10 border-red-500/20", className)} data-testid="mutation-timeline-error">
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-red-400">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span className="text-sm">Failed to load history</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-2 mx-auto block"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // Empty state
  if (!mutations || mutations.length === 0) {
    return (
      <Card className={cn("glass border-white/10", className)} data-testid="mutation-timeline-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-400" />
            Change History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-32 text-slate-400">
            <GitCommit className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No changes recorded yet</p>
            <p className="text-xs text-slate-500 mt-1">
              Edits will appear here as you modify the funnel
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Stats
  const appliedCount = mutations.filter(m => m.status === 'applied').length;
  const undoneCount = mutations.filter(m => m.status === 'undone').length;
  const aiCount = mutations.filter(m => m.actor_type === 'ai' || m.actor_type === 'system').length;
  
  return (
    <Card className={cn("glass border-white/10 flex flex-col", maxHeight ? "h-full" : "", className)} data-testid="mutation-timeline">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-400" />
            Change History
            <Badge className="ml-2 text-xs bg-white/5 text-slate-400 border-white/10">
              {mutations.length} changes
            </Badge>
          </CardTitle>
          
          {/* Quick stats */}
          {!compact && (
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Check className="h-3 w-3 text-green-400" />
                {appliedCount} applied
              </span>
              {undoneCount > 0 && (
                <span className="flex items-center gap-1">
                  <Undo2 className="h-3 w-3 text-amber-400" />
                  {undoneCount} undone
                </span>
              )}
              {aiCount > 0 && (
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-purple-400" />
                  {aiCount} AI
                </span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 flex-1 overflow-hidden">
        <ScrollArea className="pr-4 h-full" style={{ maxHeight: maxHeight || "none" }}>
          <div className="py-2">
            {mutations.map((mutation, idx) => (
              <MutationEntry
                key={mutation.mutation_id}
                mutation={mutation}
                funnelId={funnelId}
                isFirst={idx === 0}
                isLast={idx === mutations.length - 1}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

/**
 * Compact inline version for embedding in other views
 */
export function MutationTimelineCompact({ funnelId, limit = 5 }) {
  const { data: mutations, isLoading } = useMutationHistory(funnelId, limit);
  
  if (isLoading || !mutations || mutations.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-2" data-testid="mutation-timeline-compact">
      {mutations.slice(0, limit).map((mutation) => {
        const time = formatTimestamp(mutation.created_at);
        const statusConfig = STATUS_CONFIG[mutation.status] || STATUS_CONFIG.applied;
        
        return (
          <div 
            key={mutation.mutation_id}
            className="flex items-center justify-between text-xs p-2 rounded bg-white/[0.02] border border-white/[0.05]"
          >
            <span className="text-slate-300 truncate flex-1">
              {mutation.summary}
            </span>
            <span className={cn("ml-2", statusConfig.color)}>
              {time.relative}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default MutationTimeline;
